import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { teacherApi, RadarResponse, RadarConfigResponse } from '@/services/api/teacher'
import { getSavedTimeRangeDays, saveTimeRangeDays, DEFAULT_TIME_RANGE_DAYS } from '@/utils/radarStorage'
import { onCascadeStateChanged } from '@/utils/cascadeEvents'
import { useRadarUserPrefs } from '@/components/radar/hooks/useRadarUserPrefs'

export function useTeacherRadar() {
  const [radarData, setRadarData] = useState<RadarResponse | null>(null)
  const [radarConfig, setRadarConfig] = useState<RadarConfigResponse | null>(null)
  // Cache saved value in ref to avoid double read
  const savedDaysRef = useRef<number | null>(null)
  if (savedDaysRef.current === null) {
    savedDaysRef.current = getSavedTimeRangeDays('teacher')
  }
  // Load saved time range from localStorage, fallback to 60 days
  const [timeRangeDays, setTimeRangeDays] = useState<number>(() => {
    return savedDaysRef.current ?? DEFAULT_TIME_RANGE_DAYS
  })
  const [isLoadingRadar, setIsLoadingRadar] = useState(false)
  const [isLoadingConfig, setIsLoadingConfig] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  // Per-user radar prefs (filter + server-side time-range persistence) — filter-fix-v2
  const radarPrefs = useRadarUserPrefs('teacher', teacherApi, radarConfig?.default_time_range_days)

  // Request sequencing: only the latest radar fetch may commit state (latest-wins).
  const reqGenRef = useRef(0)
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMountedRef = useRef(true)
  const serverRangeAppliedRef = useRef(false)

  const fetchRadarConfig = useCallback(async () => {
    setIsLoadingConfig(true)
    try {
      const cfg = await teacherApi.getRadarConfig()
      setRadarConfig(cfg)
      // Use cached saved value if available, otherwise use config default
      // Clamp to config limits to ensure value is valid
      if (!serverRangeAppliedRef.current) {
        const initialDays = savedDaysRef.current ?? cfg.default_time_range_days
        const clampedDays = Math.max(
          cfg.min_time_range_days,
          Math.min(cfg.max_time_range_days, initialDays)
        )
        setTimeRangeDays(clampedDays)
      }
    } catch (e: any) {
      console.error('[TeacherRadar] Config fetch error:', e)
    } finally {
      setIsLoadingConfig(false)
    }
  }, [])

  const fetchRadarBubbles = useCallback(async (days: number) => {
    // Cancel any in-flight request and start a new generation (latest-wins).
    abortRef.current?.abort()
    const gen = ++reqGenRef.current
    const controller = new AbortController()
    abortRef.current = controller

    if (gen === reqGenRef.current && isMountedRef.current) {
      setIsLoadingRadar(true)
    }
    try {
      const data = await teacherApi.getTeacherRadarBubbles(days, true, controller.signal)
      if (gen !== reqGenRef.current || !isMountedRef.current) return
      setRadarData(data)
      setError(null)
    } catch (e: unknown) {
      const err = e as { name?: string; code?: string; statusCode?: number }
      const aborted =
        err?.name === 'CanceledError' ||
        err?.name === 'AbortError' ||
        err?.code === 'ERR_CANCELED'
      if (aborted || gen !== reqGenRef.current) return
      console.error('[TeacherRadar] Bubbles fetch error:', e)
      setError(`Radar konnte nicht geladen werden (${err?.statusCode || 'Unbekannt'})`)
    } finally {
      if (gen === reqGenRef.current && isMountedRef.current) {
        setIsLoadingRadar(false)
      }
    }
  }, [])

  const handleTimeRangeChange = useCallback((newDays: number) => {
    if (radarPrefs.isLocked) return // locked teacher: keep the standard view
    // UI (slider) and persistence react immediately; the network fetch is debounced
    // so rapid dragging does not fire one request per tick. The initial load is
    // fetched immediately and separately (see the radarConfig effect below), so this
    // only affects user-driven slider changes.
    setTimeRangeDays(newDays)
    saveTimeRangeDays(newDays, 'teacher')
    radarPrefs.setTimeRange(newDays) // server-side persistence (filter-fix-v2)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchRadarBubbles(newDays)
    }, 300)
  }, [fetchRadarBubbles, radarPrefs])

  // Fetch config on mount
  useEffect(() => {
    fetchRadarConfig()
  }, [fetchRadarConfig])

  // Fetch bubbles when config is loaded and timeRangeDays changes
  const timeRangeRef = useRef(timeRangeDays)
  timeRangeRef.current = timeRangeDays

  useEffect(() => {
    if (!radarPrefs.isLoaded) return
    const days = radarPrefs.timeRangeDays
    if (days == null) {
      serverRangeAppliedRef.current = true
      return
    }
    const min = radarConfig?.min_time_range_days ?? 1
    const max = radarConfig?.max_time_range_days ?? 365
    const clamped = Math.max(min, Math.min(max, days))
    serverRangeAppliedRef.current = true
    if (clamped === timeRangeRef.current) return
    setTimeRangeDays(clamped)
    saveTimeRangeDays(clamped, 'teacher')
    fetchRadarBubbles(clamped)
  }, [radarPrefs.isLoaded, radarPrefs.timeRangeDays, radarConfig, fetchRadarBubbles])

  useEffect(() => {
    if (radarConfig) {
      fetchRadarBubbles(timeRangeRef.current)
    }
  }, [radarConfig])

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (!radarConfig) return
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        fetchRadarBubbles(timeRangeRef.current)
      }
    }, 60000)
    return () => clearInterval(interval)
  }, [radarConfig, fetchRadarBubbles])

  useEffect(() => {
    return onCascadeStateChanged(() => {
      if (document.visibilityState === 'visible') {
        fetchRadarBubbles(timeRangeRef.current)
      }
    })
  }, [fetchRadarBubbles])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchRadarBubbles(timeRangeRef.current)
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [fetchRadarBubbles])

  // Cancel any in-flight request on unmount. Reset isMounted in setup so
  // React.StrictMode's dev double-mount does not leave the flag stuck on false.
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      abortRef.current?.abort()
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const outgoingBubbles = useMemo(
    () => radarData?.bubbles.filter(b => b.bubble_type !== 'incoming') ?? [],
    [radarData],
  )
  const incomingBubbles = useMemo(
    () => radarData?.bubbles.filter(b => b.bubble_type === 'incoming') ?? [],
    [radarData],
  )

  return {
    radarData,
    radarConfig,
    timeRangeDays,
    isLoadingRadar,
    isLoadingConfig,
    error,
    setError,
    message,
    setMessage,
    outgoingBubbles,
    incomingBubbles,
    fetchRadarBubbles,
    handleTimeRangeChange,
    // Filter prefs (filter-fix-v2)
    filterState: radarPrefs.filterState,
    setFilter: radarPrefs.setFilter,
    resetFilter: radarPrefs.resetFilter,
    isFilterLocked: radarPrefs.isLocked,
    filterDefaultState: radarPrefs.defaultState,
  }
}
